import { useEffect, useState } from "react";
import { notify } from "@/components/ui/notification";
import { useNavigate } from "react-router-dom";
import { FeedbackDialog } from "@/components/dashboard/FeedbackDialog";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import logoLight from "@/assets/png/logo.png";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  ArrowLeft01Icon,
  Settings02Icon,
  Copy01Icon,
  Edit02Icon,
  StarIcon,
  DarkModeIcon,
  Sun01Icon,
  Moon01Icon,
  ComputerIcon,
  HelpCircleIcon,
  ArrowUpRight03Icon,
  MessageMultiple01Icon,
  Coins01Icon,
} from "@hugeicons/core-free-icons";
import { createProjectsApi } from "@/lib/api/projects";
import type { ProjectListItem } from "@/lib/api/projects";
import { useCreditsBalance } from "@/lib/hooks/use-credits";
import { RenameProjectDialog } from "./RenameProjectDialog";

interface ProjectNavbarProps {
  leftPanelWidth: number;
  projectId: string;
  projectName?: string;
  isStarred?: boolean;
  getToken: () => Promise<string | null>;
}

type Theme = "light" | "dark" | "system";

function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return "dark";
    return (localStorage.getItem("theme") as Theme) ?? "dark";
  });

  useEffect(() => {
    const root = document.documentElement;
    localStorage.setItem("theme", theme);

    if (theme === "system") {
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      root.classList.toggle("dark", prefersDark);
    } else {
      root.classList.toggle("dark", theme === "dark");
    }
  }, [theme]);

  return { theme, setTheme: setThemeState };
}

export function ProjectNavbar({
  leftPanelWidth,
  projectId,
  projectName: initialProjectName,
  isStarred: initialIsStarred = false,
  getToken,
}: ProjectNavbarProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();
  const { data: balance } = useCreditsBalance();
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [projectName, setProjectName] = useState(
    initialProjectName || "Untitled Project",
  );
  const [isStarred, setIsStarred] = useState(initialIsStarred);
  const [includeHistory, setIncludeHistory] = useState(false);

  const projectsApi = createProjectsApi({ getToken });

  const cloneMutation = useMutation({
    mutationFn: () => projectsApi.clone(projectId),
    onSuccess: (cloned) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setCloneDialogOpen(false);
      navigate(`/projects/${cloned.id}`);
    },
    onError: () => {
      notify.error("Could not duplicate project", { description: "Something went wrong. Try again." });
    },
  });

  const starMutation = useMutation({
    mutationFn: () => projectsApi.star(projectId),
    onMutate: () => {
      // Optimistic update — flip immediately before response
      setIsStarred((prev) => !prev);
      const prevData = queryClient.getQueryData<ProjectListItem[]>([
        "projects",
      ]);
      queryClient.setQueryData<ProjectListItem[]>(["projects"], (old = []) =>
        old.map((p) =>
          p.id === projectId ? { ...p, isStarred: !p.isStarred } : p,
        ),
      );
      return { prevData };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back on error
      setIsStarred((prev) => !prev);
      queryClient.setQueryData(["projects"], ctx?.prevData);
      notify.error("Could not update starred", { description: "The change was rolled back. Try again." });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });

  const handleClone = () => {
    cloneMutation.mutate();
  };

  return (
    <nav className="flex h-12 w-full shrink-0 items-center">
      {/* Left section — synced with chat panel width */}
      <div
        className="flex h-full items-center gap-2 px-4 justify-between"
        style={{ width: `${leftPanelWidth}%` }}
      >
        {/* Project dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-lg outline-none px-2 py-1.5 hover:opacity-70 transition-colors cursor-pointer">
              <img
                src={logoLight}
                alt="Rayloop AI"
                className="size-6 rounded-sm dark:invert"
              />
              <span className="truncate text-sm font-medium text-foreground">
                {projectName}
              </span>
              <HugeiconsIcon
                icon={ArrowDown01Icon}
                className="size-4 text-muted-foreground"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="min-w-64"
            align="start"
            sideOffset={8}
          >
            <DropdownMenuItem onClick={() => navigate("/dashboard")}>
              <HugeiconsIcon icon={ArrowLeft01Icon} className="size-4" />
              Go to Dashboard
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={() => navigate(`/projects/${projectId}/settings`)}
            >
              <HugeiconsIcon icon={Settings02Icon} className="size-4" />
              Settings
              <DropdownMenuShortcut>⌘K</DropdownMenuShortcut>
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => setCloneDialogOpen(true)}>
              <HugeiconsIcon icon={Copy01Icon} className="size-4" />
              Clone this project
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => setRenameDialogOpen(true)}>
              <HugeiconsIcon icon={Edit02Icon} className="size-4" />
              Rename project
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => starMutation.mutate()}>
              <HugeiconsIcon
                icon={StarIcon}
                className={`size-4 ${isStarred ? "fill-amber-400 text-amber-400" : ""}`}
              />
              {isStarred ? "Unstar project" : "Star project"}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {balance && (
              <DropdownMenuItem onClick={() => navigate("/settings?upgrade=1")}>
                <HugeiconsIcon icon={Coins01Icon} className="size-4" />
                {balance.total.toLocaleString()} credits
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <HugeiconsIcon icon={DarkModeIcon} className="size-4" />
                Appearance
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => setTheme("light")}>
                  <HugeiconsIcon icon={Sun01Icon} className="size-4" />
                  Light
                  {theme === "light" && (
                    <span className="ml-auto text-xs">✓</span>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("dark")}>
                  <HugeiconsIcon icon={Moon01Icon} className="size-4" />
                  Dark
                  {theme === "dark" && (
                    <span className="ml-auto text-xs">✓</span>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("system")}>
                  <HugeiconsIcon icon={ComputerIcon} className="size-4" />
                  System
                  {theme === "system" && (
                    <span className="ml-auto text-xs">✓</span>
                  )}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuItem onClick={() => window.open("/support", "_blank")}>
              <HugeiconsIcon icon={HelpCircleIcon} className="size-4" />
              Help
              <HugeiconsIcon
                icon={ArrowUpRight03Icon}
                className="ml-auto size-4"
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Right section — fills remaining space */}
      <div className="flex h-full flex-1 items-center justify-end gap-2 px-4">
        <button
          onClick={() => setFeedbackOpen(true)}
          title="Send feedback"
          className="text-muted-foreground hover:text-foreground hover:bg-accent flex size-7 items-center justify-center rounded-md transition-colors"
        >
          <HugeiconsIcon icon={MessageMultiple01Icon} className="size-4" />
        </button>
        <Button variant="secondary" size="sm" className="h-7 text-xs">
          Share
        </Button>
      </div>

      {/* Rename Project Dialog */}
      <RenameProjectDialog
        open={renameDialogOpen}
        onOpenChange={setRenameDialogOpen}
        projectId={projectId}
        currentName={projectName}
        getToken={getToken}
        onSuccess={(newName) => setProjectName(newName)}
      />

      {/* Feedback Dialog */}
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />

      {/* Clone Project Dialog */}
      <Dialog open={cloneDialogOpen} onOpenChange={setCloneDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-surface-raised border-white/10 p-6">
          <div className="flex items-center gap-3 mb-2">
            <img
              src={logoLight}
              alt="Rayloop AI"
              className="size-12 rounded-lg invert dark:invert-0"
            />
          </div>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-surface-raised-text">
              Clone project
            </DialogTitle>
            <DialogDescription className="text-surface-raised-text-muted">
              By cloning a project, you will create a copy that you own.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center justify-between">
              <label
                htmlFor="include-history"
                className="text-sm font-medium text-surface-raised-text cursor-pointer"
              >
                Include project history
              </label>
              <Switch
                id="include-history"
                checked={includeHistory}
                onCheckedChange={setIncludeHistory}
              />
            </div>
          </div>
          <DialogFooter className="bg-transparent border-t-0 -mx-6 -mb-6 p-6 pt-4">
            <Button
              variant="outline"
              onClick={() => setCloneDialogOpen(false)}
              disabled={cloneMutation.isPending}
              className="bg-transparent border-white/20 text-surface-raised-text hover:bg-surface-raised-hover"
            >
              Cancel
            </Button>
            <Button
              onClick={handleClone}
              disabled={cloneMutation.isPending}
              className="bg-white text-black hover:bg-white/90"
            >
              {cloneMutation.isPending ? "Cloning…" : "Clone"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </nav>
  );
}
